import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Droplets, CloudRain, Mountain, ArrowRight, Sparkles } from "lucide-react";
import { usePublicPricing } from "@/lib/usePublicPricing";

const OPTIONS = [
  {
    id: "basic",
    icon: Droplets,
    question: "Concerned about drinking water safety?",
    recommendation: "AWARE 4-Point",
    featured: false,
  },
  {
    id: "tank",
    icon: CloudRain,
    question: "Have a rainwater tank?",
    recommendation: "AWARE Tank 360",
    featured: true,
  },
  {
    id: "bore",
    icon: Mountain,
    question: "Use bore water?",
    recommendation: "AWARE Bore 360",
    featured: false,
  },
];

// Fallback SSoT-aligned snapshot used only until /api/pricing arrives.
// All three fields collapse to the same all-inclusive tier to match the
// backend's `pricing_snapshot()` under the Feb 2026 rollout.
const FALLBACK = {
  basic: { retail: 129, online_prepaid: 129 },
  tank:  { retail: 209, online_prepaid: 209 },
  bore:  { retail: 229, online_prepaid: 229 },
};

export function TestPicker() {
  const { enabled: discountEnabled, priceFor } = usePublicPricing();
  return (
    <section id="picker" className="py-12 lg:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">Not sure which to choose?</span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.05]">
            Which test is <span className="water-text">right for me?</span>
          </h2>
          <p className="mt-4 text-base text-slate-600">Pick the situation that sounds most like yours — we'll point you to the right test.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 lg:gap-5">
          {OPTIONS.map((o, i) => {
            const Icon = o.icon;
            const live = priceFor(o.id) || {};
            const retail   = live.retail        ?? FALLBACK[o.id].retail;
            const online   = live.online_prepaid ?? (discountEnabled ? FALLBACK[o.id].online_prepaid : retail);
            const price    = discountEnabled ? online : retail;
            const subLine  = `$${price}`;
            return (
              <motion.div
                key={o.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`test-picker-${o.id}`}
                className={`relative rounded-3xl p-7 lg:p-8 transition-all hover:-translate-y-1 ${
                  o.featured
                    ? "bg-slate-900 text-white shadow-2xl ring-2 ring-cyan-500"
                    : "bg-slate-50 border border-slate-100 hover:border-cyan-200 hover:shadow-lg hover:bg-white"
                }`}
              >
                {o.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500 text-white text-[11px] font-bold uppercase tracking-wider shadow-lg whitespace-nowrap">
                    <Sparkles className="w-3 h-3" /> Most Popular
                  </div>
                )}
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                  o.featured ? "bg-cyan-500/20 text-cyan-300" : "bg-white border border-slate-100 text-cyan-600"
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className={`mt-5 font-display text-lg font-semibold leading-tight ${o.featured ? "text-white" : "text-slate-900"}`}>
                  {o.question}
                </h3>
                <div className={`mt-5 pt-5 border-t ${o.featured ? "border-white/10" : "border-slate-200/70"}`}>
                  <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${o.featured ? "text-cyan-400" : "text-cyan-600"}`}>Recommended</div>
                  <div className={`font-display font-semibold mt-1 ${o.featured ? "text-white" : "text-slate-900"}`}>{o.recommendation}</div>
                  <div className={`text-xs mt-1 ${o.featured ? "text-slate-300" : "text-slate-500"}`}>{subLine}</div>
                </div>
                <Link
                  to={`/book-water-test?test=${o.id}`}
                  data-testid={`test-picker-cta-${o.id}`}
                  className={`group mt-5 inline-flex items-center gap-1.5 text-sm font-semibold ${
                    o.featured ? "text-cyan-300 hover:text-white" : "text-cyan-600 hover:text-cyan-700"
                  }`}
                >
                  Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
